import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { superAdminApi } from '../services/api';
import Maintenance from '../pages/Maintenance';
import LoadingSpinner from './ui/LoadingSpinner';

/**
 * Component that checks if the platform is in maintenance mode
 * and shows the maintenance page to non-admin users
 */
const MaintenanceCheck = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Routes that should always be accessible during maintenance mode
  const ALLOWED_ROUTES = [
    '/auth/login',
    '/auth/register',
    '/admin-access', // Special admin access route
  ];

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        // Check if current user is admin FIRST
        let userIsAdmin = false;
        if (user) {
          userIsAdmin =
            user.active_role === 'admin' ||
            (user.roles && user.roles.includes('admin'));
          setIsAdmin(userIsAdmin);
        } else {
          setIsAdmin(false);
        }

        const response = await superAdminApi.getPublicSettings();

        if (response.data.success) {
          const maintenanceEnabled = response.data.data.maintenance_mode || false;
          setIsMaintenanceMode(maintenanceEnabled);
        }
      } catch (error) {
        // Check if error is 503 (maintenance mode)
        if (error.response?.status === 503) {
          setIsMaintenanceMode(true);
        }
        console.error('Error checking maintenance mode:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Run on mount / when the user changes, then poll every 30s.
    // IMPORTANT: we do NOT toggle isLoading on re-checks and do NOT depend on the
    // route — otherwise every navigation would flash the full-screen loader and
    // remount the entire app (sidebar included). Per-route maintenance gating is
    // handled in render via `isAllowedRoute`, so the effect doesn't need the path.
    checkMaintenanceMode();

    // Re-check every 30 seconds to detect when maintenance mode is disabled
    const interval = setInterval(checkMaintenanceMode, 30000);

    return () => clearInterval(interval);
  }, [user]);

  // Check if current route is in allowed routes
  const isAllowedRoute = ALLOWED_ROUTES.some(route =>
    location.pathname.startsWith(route)
  );

  // Show loading spinner while checking
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // If maintenance mode is on
  if (isMaintenanceMode) {
    // Allow access if:
    // 1. User is admin, OR
    // 2. Current route is in allowed routes (login, register, admin-access)
    if (isAdmin || isAllowedRoute) {
      return <>{children}</>;
    }

    // Otherwise show maintenance page
    return <Maintenance />;
  }

  // Maintenance mode is off, render normally
  return <>{children}</>;
};

export default MaintenanceCheck;
